import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  User,
  Save,
  X,
  Edit3,
  Mail,
  Phone,
  MapPin,
  Star,
  Camera,
  Map,
  Check,
  Upload
} from 'react-feather';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import MembershipGuard from '../common/MembershipGuard';
import ServiceLocationMap from './ServiceLocationMap';
import axios from 'axios';
import './TalentProfile.css';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

// Extend Window interface for Google Maps
declare global {
  interface Window {
    google: any;
  }
}

interface Skill {
  name: string;
  experience_level: string;
}

interface Media {
  id: string;
  file_url: string;
  file_type: string;
}

interface TalentProfileData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  parish: string;
  bio: string;
  profilePicture?: string;
  skills: Skill[] | string;
  media: Media[];
  yearsOfExperience: number;
  hourlyRate: number;
  rating: number;
  totalReviews: number;
  completedJobs: number;
  responseTime: string;
  availability: string;
  joinDate: string;
  isVerified: boolean;
  portfolio: string[];
  certifications: string[];
  dateOfBirth?: string;
  gender?: string;
  verificationStatus: string;
  profileStatus: string;
  coverImage?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  // Try both token names for backwards compatibility
  const token = localStorage.getItem('talent_token') || localStorage.getItem('skillseek_talent_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

const TalentProfile: React.FC = () => {
  const { talent, logout } = useTalentAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'about' | 'reviews' | 'location'>('about');
  const [profile, setProfile] = useState<TalentProfileData | null>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  // Add this state for validation messages
  const [validationError, setValidationError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<TalentProfileData>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadedMedia, setUploadedMedia] = useState<File[]>([]);
  const [mediaToDelete, setMediaToDelete] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [showMap, setShowMap] = useState(false);

  // Helper function to get API base URL
  const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace(/\/api$/, '');
    }
    if (typeof window !== 'undefined') {
      const h = window.location.hostname;
      const isLocal =
        h === 'localhost' || h === '127.0.0.1' ||
        /^192\.168\./.test(h) || /^10\./.test(h) ||
        /^172\.(1[6-9]|2[0-9]|3[01])\./.test(h);
      if (isLocal) return `http://${h}:5000`;
      return 'https://api.skillseek.co';
    }
    return 'http://localhost:5000';
  };

  const normalizeMediaUrl = (url: string) => {
  if (!url) return '';
  
  if (url.startsWith('http://localhost') || url.startsWith('http://127.0.0.1')) {
    const path = url.replace(/^http:\/\/(localhost|127\.0\.0\.1):\d+/, '');
    return `${getApiBaseUrl()}${path}`;
  }
  if (url.startsWith('http')) return url;
  return `${getApiBaseUrl()}/${url.replace(/^\//, '')}`;
};

  const normalizeUrl = (imagePath: string | null | undefined, fallback: string = ''): string => {
    if (!imagePath) return fallback;
    
    let cleanPath = imagePath;
    const uploadsIndex = cleanPath.indexOf('/uploads/');
    if (uploadsIndex !== -1 && (cleanPath.startsWith('http://') || cleanPath.startsWith('https://'))) {
      cleanPath = cleanPath.substring(uploadsIndex + 1);
    }
    
    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) return cleanPath;
    if (cleanPath.startsWith('/uploads/') || cleanPath.startsWith('uploads/')) {
      const cleanSub = cleanPath.startsWith('/') ? cleanPath.substring(1) : cleanPath;
      return `${getApiBaseUrl()}/${cleanSub}`;
    }
    return `${getApiBaseUrl()}/uploads/${cleanPath}`;
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the talent auth profile endpoint instead of admin endpoint
      const response = await api.get('/talent/auth/profile');

      if (response.data.success) {
        const talentData = response.data.talent;

        console.log(talentData, 'asssssssssssssssssss');
        // Transform the backend data to match our interface
        const transformedProfile: TalentProfileData = {
          id: talentData.id.toString(),
          firstName: talentData.first_name || '',
          lastName: talentData.last_name || '',
          email: talentData.email || '',
          phone: talentData.phone || '',
          address: talentData.address || '',
          parish: talentData.parish || '',
          bio: talentData.bio || '',
          profilePicture: normalizeUrl(talentData.profile_picture),
          skills: Array.isArray(talentData.skills) ? talentData.skills : [],
          media: Array.isArray(talentData.media) ? talentData.media : [],
          yearsOfExperience: talentData.experience_years || 0,
          hourlyRate: parseFloat(talentData.hourly_rate) || 0,
          rating: talentData.rating || 0,
          totalReviews: talentData.total_reviews || 0,
          completedJobs: talentData.total_jobs_completed || 0,
          responseTime: '2 hours', // Default value - you may want to add this field
          availability: talentData.profile_status === 'active' ? 'Available' : 'Unavailable',
          joinDate: talentData.created_at || '',
          isVerified: talentData.verification_status === 'verified',
          portfolio: [], // You may want to fetch this from a separate endpoint
          certifications: [], // You may want to fetch this from a separate endpoint
          dateOfBirth: talentData.date_of_birth || '',
          gender: talentData.gender || '',
          verificationStatus: talentData.verification_status || 'pending',
          profileStatus: talentData.profile_status || 'active',
          coverImage: normalizeUrl(talentData.cover_image, '/assets/images/about-bnr.png'),
          latitude: talentData.latitude ? parseFloat(talentData.latitude) : undefined,
          longitude: talentData.longitude ? parseFloat(talentData.longitude) : undefined,
          city: talentData.city || ''
        };

        console.log('Transformed profile cover image:', transformedProfile.coverImage);

        setProfile(transformedProfile);
        setEditForm(transformedProfile);

        // Fetch recent reviews
        fetchReviews(talentData.id);
      } else {
        throw new Error(response.data.message || 'Failed to fetch profile');
      }
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async (workerId: string) => {
    try {
      const response = await api.get(`/talent/auth/reviews?limit=3`);
      if (response.data.success) {
        setReviews(response.data.reviews || []);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm(profile || {});
    setUploadedMedia([]);
    setMediaToDelete([]);

    // Clean up image states
    setCoverImageFile(null);
    setProfileImageFile(null);
    setCoverImagePreview(null);
    setProfileImagePreview(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      console.log('Saving profile with images:', {
        hasProfileImageFile: !!profileImageFile,
        hasCoverImageFile: !!coverImageFile,
        profileImagePreview: !!profileImagePreview,
        coverImagePreview: !!coverImagePreview
      });

      // Upload images first if any
      const uploadedImages = await uploadImages();

      console.log('Uploaded images result:', uploadedImages);

      // Upload media files if any
      const uploadedMediaUrls = await uploadMediaFiles();

      console.log('Uploaded media info:', uploadedMediaUrls);

      // Delete media files if any
      if (mediaToDelete.length > 0) {
        await deleteMediaFiles();
      }

      // Normalize skills: convert comma-separated string into an array of Skill objects
      const skillsArray =
        typeof editForm.skills === 'string'
          ? editForm.skills
            .split(',')
            .map(skill => skill.trim())
            .filter(Boolean)
            .map(skill => ({ name: skill, experience_level: 'intermediate' }))
          : editForm.skills;

      // Transform the form data back to backend format
      const updateData = {
        first_name: editForm.firstName,
        last_name: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        address: editForm.address,
        parish: editForm.parish,
        bio: editForm.bio,
        experience_years: editForm.yearsOfExperience,
        date_of_birth: editForm.dateOfBirth,
        gender: editForm.gender,
        latitude: editForm.latitude,
        longitude: editForm.longitude,
        city: editForm.city,
        skills: skillsArray,
        profile_picture: uploadedImages.profile_picture || (typeof editForm.profilePicture === 'string' ? editForm.profilePicture : null),
        cover_image: uploadedImages.cover_image || (typeof editForm.coverImage === 'string' ? editForm.coverImage : null),
        media: uploadedMediaUrls,
        hourly_rate: editForm.hourlyRate || 0
      };

      console.log('Update data being sent:', updateData);

      // Use the talent auth profile update endpoint
      const response = await api.put('/talent/auth/profile', updateData);

      if (response.data.success) {
        // Update local state with new image filenames
        const updatedProfile = {
          ...editForm,
          profilePicture: normalizeUrl(uploadedImages.profile_picture || editForm.profilePicture),
          coverImage: normalizeUrl(uploadedImages.cover_image || editForm.coverImage),
          media: [...(profile?.media.filter(m => !mediaToDelete.includes(m.id)) || []), ...uploadedMediaUrls]
        };

        console.log('Profile updated successfully, new profile data:', updatedProfile);

        setProfile(prev => ({ ...prev, ...updatedProfile } as TalentProfileData));
        setIsEditing(false);

        // Clean up file states
        setCoverImageFile(null);
        setProfileImageFile(null);
        setCoverImagePreview(null);
        setProfileImagePreview(null);
        setUploadedMedia([]);
        setMediaToDelete([]);

        // Optionally refresh the profile data
        await fetchProfile();
      } else {
        throw new Error(response.data.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setError(error.response?.data?.message || error.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof TalentProfileData, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSkillsChange = (skillInputs: string) => {
    setEditForm(prev => ({ ...prev, skills: skillInputs }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'cover') => {
    const file = event.target.files?.[0];
    if (file) {
      console.log(`Uploading ${type} image:`, file.name, file.size);
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'profile') {
          setProfileImagePreview(reader.result as string);
          setProfileImageFile(file);
        } else {
          console.log('Setting cover image preview and file');
          setCoverImagePreview(reader.result as string);
          setCoverImageFile(file);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Add this utility function
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleMediaUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newMediaFiles = Array.from(files);

      // Validation
      const maxSize = 10 * 1024 * 1024; 
      const validFiles: File[] = [];
      const errors: string[] = [];

      newMediaFiles.forEach(file => {
        // Check file type
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');

        if (!isImage && !isVideo) {
          errors.push(`${file.name}: Only images (JPEG, PNG, GIF) and videos (MP4, MOV) are allowed`);
          return;
        }

        // Check file size
        if (file.size > maxSize) {
            Swal.fire({
        title: 'File Too Large!',
        text: `${file.name} is larger than  (${formatFileSize(file.size)})  Please choose a smaller file.`,
        icon: 'warning',
        confirmButtonColor: '#dc3545'
      });
          return;
        }


        // Check specific file extensions for better security
        const extension = file.name.split('.').pop()?.toLowerCase();
        const allowedImageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
        const allowedVideoExtensions = ['mp4', 'mov', 'avi', 'wmv', 'webm'];

        if (isImage && extension && !allowedImageExtensions.includes(extension)) {
          errors.push(`${file.name}: Image format not supported. Use JPEG, PNG, or GIF`);
          return;
        }

        if (isVideo && extension && !allowedVideoExtensions.includes(extension)) {
          errors.push(`${file.name}: Video format not supported. Use MP4 or MOV`);
          return;
        }

        validFiles.push(file);
      });

      // Show errors
      if (errors.length > 0) {
        setValidationError(
          `Some files couldn't be uploaded:\n${errors.join('\n')}`
        );

        // Auto-dismiss error after 5 seconds
        setTimeout(() => setValidationError(null), 8000);
      }

      // Add valid files
      if (validFiles.length > 0) {
        setUploadedMedia(prev => [...prev, ...validFiles]);
      }

      // Reset input
      event.target.value = '';
    }
  };

  const handleDeleteMedia = (mediaId: string) => {
    setMediaToDelete(prev => [...prev, mediaId]);
  };

  const uploadImages = async () => {
    console.log('Upload images called with:', {
      profileImageFile: profileImageFile?.name,
      coverImageFile: coverImageFile?.name
    });

    if (!profileImageFile && !coverImageFile) {
      return { profile_picture: null, cover_image: null };
    }

    const formData = new FormData();

    if (profileImageFile) {
      formData.append('profile_picture', profileImageFile);
      console.log('Added profile image to FormData');
    }

    if (coverImageFile) {
      formData.append('cover_image', coverImageFile);
      console.log('Added cover image to FormData');
    }

    try {
      setUploadingImages(true);
      const response = await api.post('/talent/auth/profile/upload-images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Upload response:', response.data);

      if (response.data.success) {
        return response.data.files;
      } else {
        throw new Error(response.data.message || 'Failed to upload images');
      }
    } catch (error: any) {
      console.error('Error uploading images:', error);
      throw error;
    } finally {
      setUploadingImages(false);
    }
  };

  const uploadMediaFiles = async () => {
    if (uploadedMedia.length === 0) {
      return [];
    }

    const formData = new FormData();

    uploadedMedia.forEach((file, index) => {
      formData.append(`media`, file);
    });

    try {
      const response = await api.post('/talent/auth/profile/upload-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        return response.data.media || [];
      } else {
        throw new Error(response.data.message || 'Failed to upload media');
      }
    } catch (error: any) {
      console.error('Error uploading media:', error);
      throw error;
    }
  };

  const deleteMediaFiles = async () => {
    try {
      const response = await api.delete('/talent/auth/profile/delete-media', {
        data: { mediaIds: mediaToDelete }
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete media');
      }
    } catch (error: any) {
      console.error('Error deleting media:', error);
      throw error;
    }
  };

  // No changes needed here, removed legacy map initialization

  // Initialize Google Places Autocomplete for address field
  useEffect(() => {
    if (!isEditing || !addressInputRef.current || !window.google || !window.google.maps) return;

    // Initialize autocomplete
    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: ['jm', 'us', 'ca', 'gb'] }, // Jamaica, US, Canada, UK
    });

    autocompleteRef.current = autocomplete;

    // Handle place selection
    const placeChangedListener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      if (place.formatted_address) {
        // Update the address field
        handleInputChange('address', place.formatted_address);

        // Try to extract city/parish from address components
        if (place.address_components) {
          for (const component of place.address_components) {
            // Look for locality (city) or administrative_area_level_1 (state/parish)
            if (component.types.includes('locality')) {
              // Check if it matches a Jamaican parish
              const parishMatch = [
                'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
                'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
                'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
              ].find(parish => component.long_name.includes(parish) || parish.includes(component.long_name));

              if (parishMatch) {
                handleInputChange('parish', parishMatch);
              }
            }
          }
        }
      }
    });

    // Cleanup
    return () => {
      if (window.google && window.google.maps && window.google.maps.event) {
        window.google.maps.event.removeListener(placeChangedListener);
      }
    };
  }, [isEditing]);

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        size={16}
        fill={index < rating ? '#fbbf24' : 'none'}
        color={index < rating ? '#fbbf24' : '#cbd5e1'}
      />
    ));
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-error">
        <p>Error: {error}</p>
        <button onClick={fetchProfile} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  if (!profile) {
    return <div className="profile-error">No profile data available</div>;
  }

  const today = new Date().toISOString().split("T")[0]

  return (
    <MembershipGuard>
      <style>{`
        /* ── Talent Dashboard Profile (tp-) Styles ── */
        .tp-cover-wrap { width: 100%; height: 200px; overflow: hidden; background: linear-gradient(135deg, #6366F1 0%, #0EA5E9 100%); position: relative; border-radius: 14px; }
        .tp-cover-img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .tp-cover-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; opacity: 1; transition: opacity 0.2s; z-index: 2; }
        .tp-cover-wrap:hover .tp-cover-overlay { opacity: 1; }
        .tp-page-body { margin-top: -50px; padding: 0 15px 40px; position: relative; z-index: 10; }
        .tp-card { background: #fff; border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); padding: 28px; margin-bottom: 20px; border: 1px solid #F3F4F6; }
        .tp-profile-card { padding-top: 64px; position: relative; }
        .tp-avatar-wrap { position: absolute; top: -52px; left: 24px; width: 104px; height: 104px; border-radius: 50%; border: 4px solid #fff; background: #f3f4f6; box-shadow: 0 2px 10px rgba(0,0,0,0.12); z-index: 6; }
        .tp-avatar { width: 100%; height: 100%; object-fit: cover; }
        .tp-profile-top { display: flex; gap: 20px; align-items: flex-start; flex-wrap: wrap; margin-left: 120px; }
        .tp-profile-meta { flex: 1; min-width: 200px; }
        .tp-profile-name-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
        .tp-name { font-size: 22px; font-weight: 700; color: #111827; margin: 0; }
        .tp-badges { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
        .tp-badge-verified { background: #D1FAE5; color: #065F46; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; display: inline-flex; align-items: center; gap: 3px; }
        .tp-meta-row { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; margin-top: 6px; }
        .tp-meta-item { display: flex; align-items: center; gap: 5px; font-size: 13px; color: #6B7280; }
        .tp-contact-strip { display: flex; gap: 20px; flex-wrap: wrap; margin-top: 20px; padding-top: 18px; border-top: 1px solid #F3F4F6; }
        .tp-contact-item { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #6B7280; }
        .tp-card-title { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 16px; display: flex; align-items: center; gap: 8px; }
        .tp-card-count { font-size: 12px; font-weight: 500; color: #9CA3AF; background: #F3F4F6; padding: 2px 8px; border-radius: 20px; }
        .tp-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .tp-media-cell { height: 180px; border-radius: 10px; overflow: hidden; background: #F9FAFB; position: relative; border: 1px solid #E5E7EB; }
        .tp-media-asset { width: 100%; height: 100%; object-fit: cover; }
        .tp-tab-card { padding: 0; overflow: hidden; }
        .tp-tab-nav { display: flex; border-bottom: 1.5px solid #F3F4F6; padding: 0 20px; background: #FAFAFA; }
        .tp-tab-btn { padding: 14px 20px; border: none; background: none; font-size: 13px; font-weight: 600; color: #9CA3AF; cursor: pointer; border-bottom: 2.5px solid transparent; margin-bottom: -1.5px; transition: all 0.2s; }
        .tp-tab-btn:hover { color: #4B5563; }
        .tp-tab-btn.active { color: var(--skillseek-primary, #6366F1); border-bottom-color: var(--skillseek-primary, #6366F1); }
        .tp-tab-content { padding: 28px; }
        .tp-section-title { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 16px; }
        .tp-bio-text { font-size: 14px; line-height: 1.6; color: #4B5563; margin: 0 0 24px; white-space: pre-line; }
        .tp-skills-wrap { display: flex; gap: 8px; flex-wrap: wrap; }
        .tp-skill-tag { background: #EEF2F6; color: #334155; font-size: 12px; font-weight: 600; padding: 4px 10px; border-radius: 6px; text-transform: capitalize; }
        .tp-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 20px; }
        .tp-btn { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; transition: all 0.2s; }
        .tp-btn-primary { background: var(--skillseek-primary, #6366F1); color: #fff; }
        .tp-btn-primary:hover { background: #4F46E5; }
        .tp-btn-outline { background: #fff; color: #374151; border: 1.5px solid #E5E7EB; }
        .tp-btn-outline:hover { background: #F9FAFB; border-color: #D1D5DB; }
        .tp-btn-danger { background: #FEE2E2; color: #B91C1C; }
        .tp-btn-danger:hover { background: #FCA5A5; }
        .tp-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
        .tp-stat-box { background: #FAFAFA; border: 1px solid #F3F4F6; border-radius: 10px; padding: 16px; text-align: center; }
        .tp-stat-num { display: block; font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 4px; }
        .tp-stat-label { font-size: 11px; font-weight: 600; color: #6B7280; text-transform: uppercase; margin-bottom: 4px; display: block; }
        .tp-review-item { padding: 14px; border: 1px solid #F3F4F6; border-radius: 10px; margin-bottom: 12px; background: #FAFAFA; }
        .tp-details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 24px; }
        .tp-detail-item { border-bottom: 1px solid #F3F4F6; padding-bottom: 10px; }
        .tp-directions-link { margin-left: 14px; font-size: 13px; color: #6366F1; text-decoration: none; font-weight: 500; }

        .tp-input {
          padding: 10px 14px;
          border: 1.5px solid #E2E8F0;
          border-radius: 10px;
          font-size: 14px;
          font-family: inherit;
          color: #1E293B;
          background-color: #F8FAFC;
          transition: all 0.2s ease-in-out;
          flex: 1;
          min-width: 120px;
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.02);
        }
        .tp-input:focus {
          background-color: #FFFFFF;
          border-color: var(--skillseek-primary, #6366F1);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
          outline: none;
        }

        @media (max-width: 768px) {
          .tp-page-body { margin-top: -20px; padding: 0 10px 40px; }
          .tp-cover-wrap { height: 140px; }
          .tp-grid-3 { grid-template-columns: repeat(2, 1fr); }
          .tp-stats-grid { grid-template-columns: repeat(2, 1fr); }
          .tp-details-grid { grid-template-columns: 1fr; }
          
          /* Centering Avatar and stacking details on Mobile */
          .tp-avatar-wrap { 
            width: 88px; 
            height: 88px; 
            top: -44px; 
            left: 50%; 
            transform: translateX(-50%); 
          }
          .tp-card.tp-profile-card { 
            padding-top: 85px; 
            text-align: center; 
          }
          .tp-profile-top { 
            margin-left: 0;
            justify-content: center; 
            text-align: center;
          }
          .tp-profile-meta {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
          }
          .tp-profile-name-row { 
            justify-content: center; 
            flex-direction: column; 
            align-items: center; 
            width: 100%;
          }
          .tp-profile-name-row .d-flex {
            justify-content: center;
            width: 100% !important;
            max-width: 100% !important;
          }
          .tp-input {
            flex: 1 1 100% !important;
            width: 100% !important;
          }
          .tp-badges { 
            justify-content: center; 
            margin-top: 6px;
          }
          .tp-meta-row { 
            justify-content: center; 
            flex-direction: column; 
            align-items: center; 
            gap: 8px;
            width: 100%;
          }
          .tp-actions { 
            justify-content: center; 
            width: 100%; 
            gap: 10px;
          }
          .tp-btn {
            width: 100%;
            justify-content: center;
          }
          .tp-contact-strip { 
            justify-content: center; 
            flex-direction: column; 
            align-items: center; 
            gap: 10px; 
            width: 100%;
          }
          .tp-card { padding: 20px 16px; margin-bottom: 16px; }
          .tp-tab-content { padding: 20px 10px; }
          .tp-tab-nav {
            padding: 0 8px;
            overflow-x: auto;
            white-space: nowrap;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .tp-tab-nav::-webkit-scrollbar {
            display: none;
          }
          .tp-tab-btn {
            padding: 12px 10px;
            font-size: 12.5px;
            flex-shrink: 0;
            display: inline-flex !important;
            align-items: center;
            gap: 4px;
          }
        }
      `}</style>

      <div className="tp-cover-wrap">
        <img
          src={coverImagePreview || profile?.coverImage || '/assets/images/about-bnr.png'}
          alt="Cover"
          className="tp-cover-img"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
          onLoad={(e) => {
            e.currentTarget.style.display = 'block';
          }}
        />
        {isEditing && (
          <div className="tp-cover-overlay">
            <input
              type="file"
              ref={coverInputRef}
              onChange={(e) => handleImageUpload(e, 'cover')}
              accept="image/*"
              className="d-none"
            />
            <button
              className="tp-btn tp-btn-primary"
              onClick={() => coverInputRef.current?.click()}
              disabled={uploadingImages}
            >
              <Upload size={16} /> {coverImagePreview ? 'Change Cover' : 'Upload Cover'}
            </button>
          </div>
        )}
      </div>

      <div className="tp-page-body">
        
        {/* MAIN PROFILE CARD */}
        <div className="tp-card tp-profile-card">
          <div className="tp-avatar-wrap" onClick={isEditing ? () => profileInputRef.current?.click() : undefined} style={{ cursor: isEditing ? 'pointer' : 'default' }}>
            <div className="tp-avatar-inner" style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', position: 'relative' }}>
              {profileImagePreview || profile?.profilePicture ? (
                <img
                  src={profileImagePreview || profile?.profilePicture}
                  alt="Profile"
                  className="tp-avatar"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/images/user.png";
                  }}
                />
              ) : (
                <div
                  className="tp-avatar d-flex align-items-center justify-content-center fw-bold text-white"
                  style={{
                    background: 'var(--skillseek-primary, #6c5ce7)',
                    fontSize: '2rem',
                  }}
                >
                  {profile?.firstName?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
            </div>

            {isEditing && (
              <>
                <input
                  type="file"
                  ref={profileInputRef}
                  onChange={(e) => handleImageUpload(e, 'profile')}
                  accept="image/*"
                  className="d-none"
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm position-absolute bottom-0 end-0 rounded-circle p-1 shadow"
                  style={{ transform: 'translate(10%, 10%)', zIndex: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    profileInputRef.current?.click();
                  }}
                  disabled={saving}
                >
                  <Camera size={12} />
                </button>
              </>
            )}
          </div>

          <div className="tp-profile-top">
            <div className="tp-profile-meta">
              <div className="tp-profile-name-row">
                {isEditing ? (
                  <div className="d-flex gap-2 w-100 flex-wrap" style={{ maxWidth: '400px' }}>
                    <input
                      type="text"
                      className="tp-input"
                      placeholder="First Name"
                      value={editForm.firstName || ''}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                    />
                    <input
                      type="text"
                      className="tp-input"
                      placeholder="Last Name"
                      value={editForm.lastName || ''}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                    />
                  </div>
                ) : (
                  <h1 className="tp-name">{profile?.firstName} {profile?.lastName}</h1>
                )}
                
                <div className="tp-badges">
                  {profile?.isVerified && (
                    <span className="tp-badge-verified">
                      <Check size={12} /> VERIFIED TALENT
                    </span>
                  )}
                </div>
              </div>

              <div className="tp-meta-row">
                <span className="tp-meta-item">
                  <MapPin size={14} className="text-muted" />
                  {profile?.address || 'Set your location'}
                </span>
                <span className="tp-meta-item">⏱ {profile?.yearsOfExperience || 0} yrs experience</span>
                <span className="tp-meta-item">⚡ Response: {profile?.responseTime || 'Fast'}</span>
                {profile?.rating && (
                  <span className="tp-meta-item" style={{ fontWeight: 600 }}>
                    <Star size={13} fill="#fbbf24" color="#fbbf24" />
                    {parseFloat(profile.rating as any || '0').toFixed(1)} ({profile.totalReviews} reviews)
                  </span>
                )}
              </div>

              <div className="tp-actions">
                {!isEditing ? (
                  <button className="tp-btn tp-btn-primary" onClick={handleEdit}>
                    <Edit3 size={16} /> Edit Profile
                  </button>
                ) : (
                  <>
                    <button className="tp-btn tp-btn-outline" onClick={handleCancel}>
                      <X size={16} /> Cancel
                    </button>
                    <button className="tp-btn tp-btn-primary" onClick={handleSave} disabled={uploadingImages || saving}>
                      <Save size={16} /> Save Changes
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="tp-contact-strip">
            <span className="tp-contact-item">✉ {profile?.email}</span>
            {profile?.phone && <span className="tp-contact-item">📞 {profile.phone}</span>}
          </div>
        </div>

        {/* PORTFOLIO & MEDIA CARD */}
        <div className="tp-card">
          <h2 className="tp-card-title">Portfolio & Media <span className="tp-card-count">{profile?.media?.length || 0} items</span></h2>
          
          <div className="tp-grid-3">
            {profile?.media?.map((item, idx) => (
              <div key={idx} className="tp-media-cell">
                {item.file_type === 'image' ? (
                  <img src={normalizeMediaUrl(item.file_url)} alt="Media" className="tp-media-asset" />
                ) : (
                  <video src={normalizeMediaUrl(item.file_url)} controls className="tp-media-asset" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* TABS CONTAINER */}
        <div className="tp-card tp-tab-card">
          <div className="tp-tab-nav" style={{ display: 'flex', gap: '8px' }}>
            <button className={`tp-tab-btn d-flex align-items-center gap-2 ${activeTab === 'about' ? 'active' : ''}`} onClick={() => setActiveTab('about')}>
              <User size={14} /> About
            </button>
            <button className={`tp-tab-btn d-flex align-items-center gap-2 ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')}>
              <Star size={14} /> Reviews
            </button>
            <button className={`tp-tab-btn d-flex align-items-center gap-2 ${activeTab === 'location' ? 'active' : ''}`} onClick={() => setActiveTab('location')}>
              <MapPin size={14} /> Location
            </button>
          </div>

          {/* ABOUT TAB */}
          {activeTab === 'about' && (
            <div className="tp-tab-content animate-fade-in">
              <h4 className="tp-section-title">Biography</h4>
              <p className="tp-bio-text">{profile?.bio || 'No biography written yet.'}</p>

              <h4 className="tp-section-title">Skills & Expertise</h4>
              <div className="tp-skills-wrap">
                {Array.isArray(profile?.skills) && profile.skills.map((skill, idx) => (
                  <span key={idx} className="tp-skill-tag">
                    {skill.name} ({skill.experience_level})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* REVIEWS TAB */}
          {activeTab === 'reviews' && (
            <div className="tp-tab-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="tp-section-title mb-0">Client Reviews</h4>
                <button className="tp-btn tp-btn-outline btn-sm" onClick={() => navigate('/talent/reviews')}>View All</button>
              </div>

              <div className="tp-stats-grid">
                <div className="tp-stat-box">
                  <span className="tp-stat-num">{profile?.rating ? parseFloat(profile.rating as any).toFixed(1) : '0.0'}</span>
                  <span className="tp-stat-label">Overall Rating</span>
                </div>
                <div className="tp-stat-box">
                  <span className="tp-stat-num">{profile?.totalReviews || 0}</span>
                  <span className="tp-stat-label">Total Reviews</span>
                </div>
                <div className="tp-stat-box">
                  <span className="tp-stat-num">{profile?.completedJobs || 0}</span>
                  <span className="tp-stat-label">Jobs Done</span>
                </div>
              </div>

              <div className="tp-reviews-list">
                {reviews.length === 0 ? (
                  <p className="text-muted text-center py-4">No reviews received yet.</p>
                ) : (
                  reviews.map((rev, idx) => (
                    <div key={idx} className="tp-review-item">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="text-dark">{rev.reviewer_name}</strong>
                        <span className="text-warning">★ {rev.rating}</span>
                      </div>
                      <p className="mb-0 text-muted" style={{ fontSize: '13.5px' }}>{rev.review_text}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* LOCATION TAB */}
          {activeTab === 'location' && (
            <div className="tp-tab-content">
              <h4 className="tp-section-title">Service Location</h4>
              <div className="tp-details-grid">
                <div className="tp-detail-item">
                  <span className="tp-label">Address</span>
                  <strong>{profile?.address || 'Not specified'}</strong>
                </div>
                <div className="tp-detail-item">
                  <span className="tp-label">City / Town</span>
                  <strong>{profile?.city || 'Not specified'}</strong>
                </div>
                <div className="tp-detail-item">
                  <span className="tp-label">Parish / State</span>
                  <strong>{profile?.parish || 'Not specified'}</strong>
                </div>
                <div className="tp-detail-item">
                  <span className="tp-label">Coordinates</span>
                  <strong>
                    {profile?.latitude && profile?.longitude 
                      ? `${parseFloat(profile.latitude as any).toFixed(5)}, ${parseFloat(profile.longitude as any).toFixed(5)}`
                      : 'No coordinates set'
                    }
                  </strong>
                </div>
              </div>

              {profile?.latitude && profile?.longitude && (
                <div className="mt-4" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                  <ServiceLocationMap
                    isEditing={false}
                    initialCoordinates={{ lat: parseFloat(profile.latitude as any), lng: parseFloat(profile.longitude as any) }}
                    initialAddress={profile.address}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </MembershipGuard>
  );
};

export default TalentProfile;
